import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import {
  type Ordine as DatiOrdine,
  type EsitoInvioVettore,
  type RigaOrdineDettaglio,
  useInviaAlVettore,
  useOrdine,
} from '../api/ordini.js';
import { useTogliRigaOrdine } from '../api/ordini-scrittura.js';
import { indirizzoScarico, useDocumentiOrdine } from '../api/documenti.js';
import { SeApre } from '../componenti/SeApre.js';
import { Allegati } from './AllegatiOrdine.js';
import { FatturaPdf } from './FatturaPdf.js';
import { Dato } from '../componenti/Dato.js';
import { type Colonna, Tabella } from '../componenti/Tabella.js';
import { data, dataOra, giorno, importo, pastigliaPagamento, quantita } from '../formati.js';
import { ModaleAvanzamento } from './ModaleAvanzamento.js';
import { InviiBc } from './InviiBc.js';
import { ModaleCorrezioneStato } from './ModaleCorrezioneStato.js';
import { ModaleDuplicaOrdine } from './ModaleDuplicaOrdine.js';
import { ModaleDocumento } from './ModaleDocumento.js';
import { ModaleRigaOrdine } from './ModaleRigaOrdine.js';
import { ModaleTestataOrdine } from './ModaleTestataOrdine.js';
import { useSessione } from '../api/sessione.js';
import { statoOrdine, t } from '../i18n/lingua.js';

/**
 * La scheda di un ordine.
 *
 * In cima c'è la rotta: gli stati in fila con la data in cui l'ordine è
 * passato da ognuno. Nel v2 gli stati sono otto riquadri colorati e le date
 * stanno sparse in tre riquadri diversi che si aprono a fisarmonica — per
 * sapere se la merce è arrivata bisogna aprirne due e ricordarsi il terzo.
 *
 * Le date stimate dal vettore si scrivono in corsivo: finché la nave non
 * attracca sono previsioni, e trattarle come fatti è il modo migliore per
 * promettere al cliente una data che non si rispetta.
 */

const SCHEDE = [
  { chiave: 'dati', titolo: t('Dati principali') },
  { chiave: 'righe', titolo: t('Righe') },
  { chiave: 'spedizione', titolo: t('Spedizione') },
  { chiave: 'documenti', titolo: t('Fatture e documenti') },
  { chiave: 'storico', titolo: t('Storico') },
  { chiave: 'bc', titolo: 'Business Central' },
] as const;

type Scheda = (typeof SCHEDE)[number]['chiave'];

/**
 * SKU e listino si accorciano: sono le colonne più larghe e le meno lette, e
 * per intero spingevano fuori dallo schermo «Modifica» e «Togli».
 */
const COLONNE_RIGHE: Colonna<RigaOrdineDettaglio>[] = [
  { chiave: 'sku', titolo: 'SKU', monospazio: true, larghezzaMax: 210 },
  /**
   * Il numero dell'articolo in Business Central, accanto allo SKU: è quello
   * con cui la riga si ritrova in BC. Chiesto da Rita il 26 ottobre 2026,
   * primo giorno di lavoro sulla 3.0.
   */
  {
    chiave: 'bcNumber',
    titolo: t('Numero BC'),
    monospazio: true,
    larghezzaMax: 140,
    cella: (r) => r.bcNumber ?? <span className="dim">—</span>,
  },
  {
    chiave: 'description',
    titolo: t('Descrizione'),
    cella: (r) => (
      <>
        {r.description ?? <span className="dim">—</span>}
        {r.crossReference && (
          <span className="dim" style={{ marginLeft: 6, fontSize: 11 }}>
            {t('rif. {codice}', { codice: r.crossReference })}
          </span>
        )}
      </>
    ),
  },
  {
    chiave: 'quantity',
    titolo: t('Quantità'),
    allineamento: 'destra',
    monospazio: true,
    cella: (r) => quantita(r.quantity),
  },
  {
    chiave: 'priceExw',
    titolo: t('Prezzo EXW'),
    allineamento: 'destra',
    monospazio: true,
    cella: (r) => importo(r.priceExw),
  },
  {
    chiave: 'costDdp',
    titolo: t('Costo DDP'),
    allineamento: 'destra',
    monospazio: true,
    cella: (r) => importo(r.costDdp),
  },
  {
    chiave: 'discountPercentage1',
    titolo: t('Sconti'),
    allineamento: 'destra',
    monospazio: true,
    // tre sconti in cascata: si scrivono come li scrive chi tratta il prezzo
    cella: (r) => {
      const sconti = [r.discountPercentage1, r.discountPercentage2, r.discountPercentage3]
        .filter((s) => s !== null && Number(s) !== 0)
        .map((s) => `${Number(s).toLocaleString('it-IT')}%`);
      return sconti.length ? sconti.join(' + ') : <span className="dim">—</span>;
    },
  },
  { chiave: 'pricelist', titolo: t('Listino'), larghezzaMax: 200 },
  {
    chiave: 'total',
    titolo: t('Totale'),
    allineamento: 'destra',
    monospazio: true,
    cella: (r) => importo(r.total),
  },
];

/**
 * Cosa dire dopo un invio.
 *
 * I due vettori funzionano in modo diverso e la frase deve dirlo: da C&L
 * torna indietro il tracking, e allora si dice cosa è cambiato; a Interglobo
 * si posa un file, e allora si dice come si chiama — che è quello che serve
 * il giorno in cui dicono di non aver ricevuto niente.
 *
 * Quando non è uscito niente, o non tutto, la frase la scrive il servizio:
 * lui sa se C&L e Interglobo sono collegati davvero.
 */
function raccontaInvio(esito: EsitoInvioVettore): string {
  if (esito.message) return esito.message;
  if (esito.esito === 'mandato') {
    return esito.fileName
      ? `${t('Mandato al vettore, file')} ${esito.fileName}`
      : t('Mandato al vettore.');
  }
  if (esito.esito === 'applicato') {
    return esito.righe > 1
      ? `${t('Mandato, e il vettore ha risposto con')} ${esito.righe} ${t('righe.')}`
      : t('Mandato, e il vettore ha risposto.');
  }
  return t('Mandato: il vettore non ha novità.');
}

export function Ordine({ id }: { id: number }) {
  const [scheda, setScheda] = useState<Scheda>('dati');
  const [avanzamentoAperto, setAvanzamentoAperto] = useState(false);
  const [correzioneAperta, setCorrezioneAperta] = useState(false);
  const [duplicaAperta, setDuplicaAperta] = useState(false);
  const [rigaAperta, setRigaAperta] = useState<RigaOrdineDettaglio | null | undefined>(undefined);
  const [daTogliere, setDaTogliere] = useState<number | null>(null);
  const dati = useOrdine(id);
  const togliRiga = useTogliRigaOrdine(id);
  const documenti = useDocumentiOrdine(id);
  const [documentoAperto, setDocumentoAperto] = useState(false);
  const inviaAlVettore = useInviaAlVettore(id);
  const [esitoVettore, setEsitoVettore] = useState<string | null>(null);
  const [testataAperta, setTestataAperta] = useState(false);
  const { data: sessione } = useSessione();
  /**
   * «Correggi lo stato» è di amministratori e manager: agli altri il servizio
   * dice di no (`crm-api/src/ordini/rotte.ts`). «Invia a BC», come nel v2, è
   * di chiunque apra l'ordine.
   */
  const puoCorreggereStato = ['administrator', 'manager'].includes(
    sessione?.user.accessLevel ?? '',
  );
  /** Quando l'ordine non è allo stato 3 il servizio si ferma e chiede. */
  const [confermaVettore, setConfermaVettore] = useState<string | null>(null);

  // con un numero non valido la lettura non parte nemmeno: «in attesa» per
  // sempre. Si aspetta solo una lettura vera, e il resto è «non disponibile»
  if (dati.isLoading) return <div className="cantiere">{t('Un momento…')}</div>;

  if (dati.error || !dati.data) {
    const messaggio =
      dati.error instanceof ErroreApi
        ? dati.error.message
        : t('Non è stato possibile aprire questo ordine.');
    return (
      <>
        <div className="page-head">
          <div>
            <Link className="back-link" to="/sales-orders">
              {t('← Torna agli ordini')}
            </Link>
            <h1 className="page-title">{t('Ordine non disponibile')}</h1>
          </div>
        </div>
        <div className="cantiere">
          <p>{messaggio}</p>
        </div>
      </>
    );
  }

  const o = dati.data!;
  const pagamento = pastigliaPagamento(o.paymentStatus);

  return (
    <>
      <div className="page-head">
        <div>
          <Link className="back-link" to="/sales-orders">
            {t('← Torna agli ordini')}
          </Link>
          <h1 className="page-title">{o.orderNumber}</h1>
          <div className="page-sub">
            <span className="num">
              {o.sectionalCode} {o.protocolNumber}
            </span>{' '}
            · {data(o.orderDate)} ·{' '}
            <Link className="cell-link" to="/customers/$id" params={{ id: String(o.customerId) }}>
              {o.customer}
            </Link>
            {o.destinationCountry && ` · ${o.destinationCountry}`}
            {o.archived && ` · ${t('archiviato')}`}
          </div>
        </div>
        <div className="page-actions">
          <span className={`badge ${pagamento.classe}`}>{pagamento.testo}</span>
          <strong style={{ fontSize: 15, fontFamily: 'var(--mono)' }}>
            {importo(o.total, o.currency)}
          </strong>
          <span className="btn-sep" />
          {/* il vettore si vede solo se riceve i dati da solo: sugli ordini
              senza automazione il pulsante non avrebbe niente da fare */}
          {o.carrierAutomation && (
            <button
              className="btn"
              disabled={inviaAlVettore.isPending}
              onClick={() => {
                setEsitoVettore(null);
                setConfermaVettore(null);
                inviaAlVettore.mutate(undefined, {
                  onSuccess: (esito) => setEsitoVettore(raccontaInvio(esito)),
                  onError: (errore) => {
                    // «non è allo stato 3» non è un rifiuto: è una domanda
                    if (errore instanceof ErroreApi && errore.codice === 'statoNonAtteso') {
                      setConfermaVettore(errore.message);
                    } else {
                      setEsitoVettore(
                        errore instanceof ErroreApi ? errore.message : t('Non è stato possibile mandarlo.'),
                      );
                    }
                  },
                });
              }}
            >
              {inviaAlVettore.isPending ? t('Invio…') : t('Invia al vettore')}
            </button>
          )}
          <button className="btn" onClick={() => setDuplicaAperta(true)}>
            {t('Duplica')}
          </button>
          {/* correggere lo stato non è avanzare, e non deve sembrarlo:
              sta accanto ma spento di colore, e chiede un motivo */}
          {puoCorreggereStato && (
            <button className="btn" onClick={() => setCorrezioneAperta(true)}>
              {t('Correggi lo stato')}
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setAvanzamentoAperto(true)}>
            {t('Avanza di stato')}
          </button>
        </div>
      </div>

      {confermaVettore && (
        <div className="avviso-riga">
          <span>{confermaVettore}</span>
          <button className="btn btn-ghost" onClick={() => setConfermaVettore(null)}>
            {t('Lascia stare')}
          </button>
          <button
            className="btn"
            onClick={() => {
              setConfermaVettore(null);
              inviaAlVettore.mutate(true, {
                onSuccess: (esito) => setEsitoVettore(raccontaInvio(esito)),
                onError: (errore) =>
                  setEsitoVettore(
                    errore instanceof ErroreApi ? errore.message : t('Non è stato possibile mandarlo.'),
                  ),
              });
            }}
          >
            {t('Mandalo lo stesso')}
          </button>
        </div>
      )}

      {esitoVettore && (
        <div className="avviso-riga">
          <span>{esitoVettore}</span>
          <button className="btn btn-ghost" onClick={() => setEsitoVettore(null)}>
            {t('Ho letto')}
          </button>
        </div>
      )}

      <Rotta ordine={o} />

      <div className="tabs">
        {SCHEDE.map((s) => (
          <button
            key={s.chiave}
            className="tab"
            aria-selected={scheda === s.chiave}
            onClick={() => setScheda(s.chiave)}
          >
            {s.titolo}
            {s.chiave === 'righe' && <span className="count">{o.lines.length}</span>}
            {s.chiave === 'documenti' && (
              <span className="count">
                  {o.invoices.length + (documenti.data?.total ?? 0) + o.attachments.length}
                </span>
            )}
            {s.chiave === 'storico' && <span className="count">{o.history.length}</span>}
          </button>
        ))}
      </div>

      {scheda === 'dati' && (
        <div className="grid grid-2">
          <div className="card">
            <div className="card-head">
              <span className="card-title">{t('Ordine')}</span>
              {/* i campi che si aprono li decide il servizio, stato per stato */}
              {o.editable.length > 0 && (
                <button
                  className="btn btn-ghost"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => setTestataAperta(true)}
                >
                  {t('Correggi')}
                </button>
              )}
            </div>
            <div className="card-body">
              <div className="fields">
                <Dato etichetta="Numero d'ordine del cliente">{o.orderNumber}</Dato>
                <Dato etichetta="Protocollo" mono>
                  {o.sectionalCode} {o.protocolNumber}
                </Dato>
                <Dato etichetta="Data dell'ordine">{data(o.orderDate)}</Dato>
                <Dato etichetta="Sezionale">{o.sectional}</Dato>
                <Dato etichetta="Cliente" pieno>
                  <Link
                    className="cell-link"
                    to="/customers/$id"
                    params={{ id: String(o.customerId) }}
                  >
                    {o.customer}
                  </Link>
                  {o.customerBcCode && (
                    <span className="dim" style={{ marginLeft: 8 }}>
                      {o.customerBcCode}
                    </span>
                  )}
                </Dato>
                <Dato etichetta="Destinazione della merce" pieno>
                  {o.destination ?? (
                    <span className="dim">{t('Nessuna: in BC va l’indirizzo del cliente')}</span>
                  )}
                  {o.destinationAddress && (
                    <div className="dim" style={{ fontSize: 12 }}>
                      {o.destinationAddress}
                      {o.destinationCity && `, ${o.destinationCity}`}
                      {o.destinationPostalCode && ` ${o.destinationPostalCode}`}
                      {o.destinationProvinceState && ` (${o.destinationProvinceState})`}
                      {o.destinationCountry && ` — ${o.destinationCountry}`}
                    </div>
                  )}
                </Dato>
                {o.notes && (
                  <Dato etichetta="Annotazioni" pieno>
                    <span style={{ whiteSpace: 'pre-wrap' }}>{o.notes}</span>
                  </Dato>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <span className="card-title">{t('Importi e pagamento')}</span>
            </div>
            <div className="card-body">
              <div className="fields">
                <Dato etichetta="Imponibile">{importo(o.taxable, o.currency)}</Dato>
                <Dato etichetta="Imposta">{importo(o.tax, o.currency)}</Dato>
                <Dato etichetta="Totale">
                  <strong>{importo(o.total, o.currency)}</strong>
                </Dato>
                <Dato etichetta="Valuta">{o.currency}</Dato>
                <Dato etichetta="Tipo di pagamento">{o.paymentType}</Dato>
                <Dato etichetta="Banca">{o.bank}</Dato>
                <Dato etichetta="Scadenza prevista">{data(o.estimatedPaymentDate)}</Dato>
                <Dato etichetta="Incassato il">{data(o.paymentDate)}</Dato>
                <Dato etichetta="Stato">
                  <span className={`badge ${pagamento.classe}`}>{pagamento.testo}</span>
                </Dato>
                <Dato etichetta="Importo incassato">{importo(o.paidAmount, o.currency)}</Dato>
                {o.penaltiesAmount && Number(o.penaltiesAmount) !== 0 && (
                  <Dato etichetta="Penali">{importo(o.penaltiesAmount, o.currency)}</Dato>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {scheda === 'righe' && (
        <div className="card" style={{ margin: '14px 20px 24px' }}>
          <div className="card-head">
            <span className="card-title">{t('Righe dell\'ordine')}</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 12 }}>
              {o.lines.length === 1 ? t('1 riga') : t('{n} righe', { n: o.lines.length })} · {t('totale')}{' '}
              <strong>{importo(o.total, o.currency)}</strong>
            </span>
            {o.linesEditable && (
              <button className="btn btn-primary" onClick={() => setRigaAperta(null)}>
                {t('Aggiungi articolo')}
              </button>
            )}
          </div>
          {/* dopo lo stato 2 le righe sono già in Business Central, che non
              le aggiorna: cambiarle qui farebbe dire due cose diverse */}
          {!o.linesEditable && (
            <p className="dim" style={{ margin: '0 16px 10px', fontSize: 12 }}>
              {o.linesDeletable
                ? t('Le righe non si aggiungono e non si cambiano più: sono già andate a Business Central. Si possono ancora togliere fino allo stato 3.')
                : t('Le righe non si toccano più: la merce è partita.')}
            </p>
          )}
          <Tabella
            colonne={[
              ...COLONNE_RIGHE,
              {
                chiave: '_azioni',
                titolo: '',
                allineamento: 'destra',
                cella: (r) => (
                  <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                    {o.linesEditable && (
                      <button className="btn btn-ghost" onClick={() => setRigaAperta(r)}>
                        {t('Modifica')}
                      </button>
                    )}
                    {/* togliere una riga cambia il totale dell'ordine: il
                        secondo clic è la conferma, senza aprire una finestra */}
                    {o.linesDeletable && (
                      <button
                        className={daTogliere === r.id ? 'btn btn-pericolo' : 'btn btn-ghost'}
                        disabled={togliRiga.isPending}
                        onClick={() => {
                          if (daTogliere === r.id) togliRiga.mutate(r.id);
                          else setDaTogliere(r.id);
                        }}
                        onBlur={() => setDaTogliere(null)}
                      >
                        {daTogliere === r.id ? t('Confermi?') : t('Togli')}
                      </button>
                    )}
                  </span>
                ),
              },
            ]}
            righe={o.lines}
            chiaveRiga={(r) => r.id}
            vuoto={t('Questo ordine non ha ancora righe: aggiungine una. Senza articoli non può avanzare di stato.')}
          />
        </div>
      )}

      {scheda === 'spedizione' && (
        <div className="grid grid-2">
          <div className="card">
            <div className="card-head">
              <span className="card-title">{t('Trasporto')}</span>
            </div>
            <div className="card-body">
              <div className="fields">
                <Dato etichetta="Vettore">{o.carrier}</Dato>
                {/* `shipCarrier` è la nave, non la compagnia: il legacy la
                    scrive lì (`vessel: row.shipcarrier`) e la riempie con il
                    tracking del vettore */}
                <Dato etichetta="Nave">{o.shipCarrier}</Dato>
                <Dato etichetta="Container / spedizione" mono>
                  {o.containerShipmentNumber}
                </Dato>
                <Dato etichetta="Sigillo" mono>
                  {o.seal}
                </Dato>
                <Dato etichetta="Inviato al vettore">
                  {o.carrierSent ? (
                    <span className="badge badge-ok">{t('sì')}</span>
                  ) : (
                    <span className="badge">{t('no')}</span>
                  )}
                </Dato>
                <Dato etichetta="Presa in carico">
                  {o.carrierAcknowledged ? (
                    <span className="badge badge-ok">{t('sì')}</span>
                  ) : (
                    <span className="badge">{t('no')}</span>
                  )}
                </Dato>
                {o.shipmentNotes && (
                  <Dato etichetta="Note di spedizione" pieno>
                    <span style={{ whiteSpace: 'pre-wrap' }}>{o.shipmentNotes}</span>
                  </Dato>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <span className="card-title">{t('Date del viaggio')}</span>
              {o.tracking && (
                <span className="dim" style={{ marginLeft: 'auto', fontSize: 11 }}>
                  {t('ultimo aggiornamento dal vettore: {quando}', { quando: dataOra(o.tracking.acquiredAt) })}
                </span>
              )}
            </div>
            <div className="card-body">
              <div className="fields">
                <Dato etichetta="Consegna richiesta dal cliente">
                  {data(o.deliveryDateRequested)}
                </Dato>
                <Dato etichetta="Merce pronta">{giorno(o.goodsReadyShippingDate)}</Dato>
                <Dato etichetta="Partenza stimata">
                  <span className="when eta">{data(o.estimatedDepartureDate)}</span>
                </Dato>
                <Dato etichetta="Merce partita">{giorno(o.goodsShippedDate)}</Dato>
                <Dato etichetta="Arrivo stimato">
                  <span className="when eta">{data(o.estimatedArrivalDate)}</span>
                </Dato>
                <Dato etichetta="Arrivo">{data(o.arrivalDate)}</Dato>
                <Dato etichetta="Consegna stimata">
                  <span className="when eta">{data(o.estimatedDeliveryDate)}</span>
                </Dato>
                <Dato etichetta="Consegna">{data(o.deliveryDate)}</Dato>
              </div>
            </div>
          </div>

          <div className="card span-2">
            <div className="card-head">
              <span className="card-title">{t('Navi e container')}</span>
            </div>
            <div className="card-body">
              {o.vessels.length === 0 ? (
                <p className="dim" style={{ margin: 0, fontSize: 13 }}>
                  {t('Nessuna nave assegnata a questo ordine.')}
                </p>
              ) : (
                o.vessels.map((n) => (
                  <div key={n.id} className="vessel">
                    <div className="vessel-head">
                      {/* quasi tutte le righe nave del legacy sono vuote: se il
                          nome non c'è, vale quello sull'ordine */}
                      <strong>{n.vessel ?? o.shipCarrier ?? t('Nave non indicata')}</strong>
                      <span className="dim" style={{ fontSize: 12 }}>
                        {t('partenza {data}', { data: data(n.departureTime ?? n.estimatedDepartureTime) })}
                        {' → '}
                        {t('arrivo {data}', { data: data(n.arrivalTime ?? n.estimatedArrivalTime) })}
                      </span>
                      {n.customsNumber && (
                        <span className="dim" style={{ fontSize: 11, marginLeft: 'auto' }}>
                          {t('bolla {numero} del {data}', { numero: n.customsNumber, data: data(n.customsDate) })}
                        </span>
                      )}
                    </div>
                    {n.containers.length > 0 && (
                      <ul className="containers">
                        {n.containers.map((c) => (
                          <li key={c.id}>
                            <span className="num">{c.container}</span>
                            <span className="dim">
                              {t('consegna {data}', { data: data(c.deliveryTime ?? c.estimatedDeliveryTime) })}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {scheda === 'documenti' && (
        <div className="grid grid-2">
          <div className="card">
            <div className="card-head">
              <span className="card-title">{t('Fatture')}</span>
            </div>
            <div className="card-body">
              {/* le due fatture di riepilogo restano perché la triangolazione
                  è reale: 16.110 ordini hanno sia la SPA sia la Inc */}
              <div className="fields">
                <Dato etichetta="Fattura SPA">
                  {o.invoiceNumberSpa && (
                    <>
                      <span className="num">{o.invoiceNumberSpa}</span>
                      <span className="dim" style={{ marginLeft: 8 }}>
                        {data(o.invoiceDateSpa)}
                      </span>
                    </>
                  )}
                </Dato>
                <Dato etichetta="Fattura Inc">
                  {o.invoiceNumberInc && (
                    <>
                      <span className="num">{o.invoiceNumberInc}</span>
                      <span className="dim" style={{ marginLeft: 8 }}>
                        {data(o.invoiceDateInc)}
                      </span>
                    </>
                  )}
                </Dato>
              </div>

              {/* il PDF sta accanto al suo numero, che è dove uno lo cerca */}
              <FatturaPdf ordine={o} societa="spa" />
              <FatturaPdf ordine={o} societa="inc" />

              {o.invoices.length > 0 && (
                <ul className="doclist">
                  {o.invoices.map((f) => (
                    <li key={f.id}>
                      <span className="num">{f.invoiceNumber}</span>
                      <span className="dim">{data(f.invoiceDate)}</span>
                      {f.issuer && <span className="badge">{f.issuer}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <Allegati ordineId={o.id} archiviato={o.archived} />

          <div className="card">
            <div className="card-head">
              <span className="card-title">{t('Documenti')}</span>
              {/* i documenti dell'ordine si leggono e si scaricano da chi vede
                  l'ordine; si aggiungono da chi lavora sui Documenti */}
              <SeApre sezione="documents">
                <button
                  className="btn btn-primary"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => setDocumentoAperto(true)}
                >
                  {t('Aggiungi documento')}
                </button>
              </SeApre>
            </div>
            <div className="card-body">
              {(documenti.data?.documents.length ?? 0) === 0 ? (
                <p className="dim" style={{ margin: 0, fontSize: 13 }}>
                  {documenti.isLoading ? t('Un momento…') : t('Nessun documento su questo ordine.')}
                </p>
              ) : (
                <ul className="doclist">
                  {documenti.data!.documents.map((d) => (
                    <li key={d.id}>
                      <SeApre sezione="documents" altrimenti={<span>{d.title}</span>}>
                        <Link className="cell-link" to="/documents/$id" params={{ id: String(d.id) }}>
                          {d.title}
                        </Link>
                      </SeApre>
                      {/* il nome vero, non `untitled.pdf`: da qui si scarica
                          senza passare dall'archivio */}
                      {d.fileName ? (
                        <a className="dim" href={indirizzoScarico(d.id)} download={d.downloadName}>
                          {d.downloadName}
                        </a>
                      ) : (
                        <span className="badge badge-warn">{t('senza file')}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {avanzamentoAperto && (
        <ModaleAvanzamento
          ordine={o}
          onChiudi={() => setAvanzamentoAperto(false)}
          // allo stato 3 l'ordine parte da solo: com'è andata si dice qui
          onInvio={(messaggio) => setEsitoVettore(messaggio)}
        />
      )}

      {testataAperta && (
        <ModaleTestataOrdine ordine={o} onChiudi={() => setTestataAperta(false)} />
      )}

      {correzioneAperta && (
        <ModaleCorrezioneStato ordine={o} onChiudi={() => setCorrezioneAperta(false)} />
      )}

      {duplicaAperta && (
        <ModaleDuplicaOrdine ordine={o} onChiudi={() => setDuplicaAperta(false)} />
      )}

      {documentoAperto && (
        <ModaleDocumento
          documento={null}
          ordineId={o.id}
          ordineNumero={o.orderNumber}
          onChiudi={() => setDocumentoAperto(false)}
        />
      )}

      {rigaAperta !== undefined && (
        <ModaleRigaOrdine ordine={o} riga={rigaAperta} onChiudi={() => setRigaAperta(undefined)} />
      )}

      {/* rimandare a mano è dei responsabili, come correggere lo stato */}
      {scheda === 'bc' && <InviiBc ordineId={o.id} />}

      {scheda === 'storico' && (
        <div className="card" style={{ margin: '14px 20px 24px' }}>
          <div className="card-head">
            <span className="card-title">{t('Storico delle modifiche')}</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11 }}>
              {t('le ultime {n}', { n: o.history.length })}
            </span>
          </div>
          <div className="card-body">
            {o.history.length === 0 ? (
              <p className="dim" style={{ margin: 0, fontSize: 13 }}>
                {t('Nessuna modifica registrata.')}
              </p>
            ) : (
              <ol className="history">
                {o.history.map((h) => (
                  <li
                    key={h.id}
                    className={
                      h.correction ? 'history-state history-correzione' : h.stateChanged ? 'history-state' : undefined
                    }
                  >
                    <span className="history-when">{dataOra(h.createdAt)}</span>
                    <span className="history-what">
                      {/* alla creazione l'ordine entra anche nel primo stato:
                          dirlo due volte di fila è rumore */}
                      {h.correction && <span className="badge badge-warn">{t('correzione')}</span>}
                      {h.kind === 'created' ? (
                        <strong>{t('Ordine creato')}</strong>
                      ) : (
                        h.stateChanged &&
                        h.state && <strong>{statoOrdine(h.state, h.stateOriginal).replace(/^\d+\.\s*/, '')}</strong>
                      )}
                      {/* una frase intera — «Salto dallo stato 3 allo stato 7»
                          — si legge com'è, non si scompone in campi */}
                      {h.note && <span className="dim"> {h.note}</span>}
                      {h.changes.length === 0 && !h.note && h.kind !== 'created' && !h.stateChanged && (
                        <span className="dim">{t('modifica')}</span>
                      )}
                      <span className="mods">
                        {h.changes.map((c, i) => (
                          <span className="mod" key={`${c.field}-${i}`}>
                            <span className="mod-field">{c.field}</span>
                            {c.from !== undefined ? (
                              <>
                                <span className="mod-old">{c.from || '(vuoto)'}</span>
                                <span className="mod-arrow">→</span>
                                <span className="mod-new">{c.to || '(vuoto)'}</span>
                              </>
                            ) : (
                              <span className="mod-new">{c.value || '(vuoto)'}</span>
                            )}
                          </span>
                        ))}
                      </span>
                    </span>
                    <span className="history-who dim">{h.operator ?? 'automatico'}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * La rotta: gli stati in fila, con la data di quando l'ordine ci è passato.
 *
 * Uno stato scavalcato non si nasconde — nella S.p.A. dal 3 si salta
 * direttamente alla fattura, ed è un fatto della storia dell'ordine.
 * Mostrarlo come «non ancora fatto» racconterebbe una cosa falsa; mostrarlo
 * come «fatto» un'altra. Gli stati a riposo della Inc invece arrivano già
 * tolti, se l'ordine non ci è passato.
 */
function Rotta({ ordine }: { ordine: DatiOrdine }) {
  return (
    <div className="voyage-card">
      <ol className="voyage">
        {ordine.route.map((p) => {
          const classe = p.current ? 'now' : p.skipped ? 'skipped' : p.done ? 'done' : 'ahead';
          return (
            <li key={p.id} className={`stop ${classe}`} title={p.nameOriginal}>
              <span className="stop-dot" aria-hidden="true">
                {p.skipped ? '~' : p.done ? '✓' : p.number}
              </span>
              <span className="stop-name">{statoOrdine(p.name, p.nameOriginal).replace(/^\d+\.\s*/, '')}</span>
              {p.reachedAt ? (
                <span className="stop-when">{data(p.reachedAt)}</span>
              ) : p.skipped ? (
                <span className="stop-when none" title={t('Questo stato è stato saltato')}>
                  {t('saltato')}
                </span>
              ) : (
                <span className="stop-when none">—</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
